import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LoginSchema } from '@harbor/shared';
import { ApiError } from '../lib/api.js';
import { useAuth } from '../state/auth.js';
import { Alert, Button, Card, Field, TextInput } from '../components/ui.js';

/**
 * Sign in.
 *
 * The form is validated with the *same* Zod schema the API uses, imported from
 * `@harbor/shared`. Client-side validation is therefore not a re-implementation
 * that can drift — it is the server's own rule, run early so the customer gets
 * the message without a round trip. The server still validates; this is an
 * optimisation, never the control.
 */
/**
 * The password every seeded account shares.
 *
 * Committed deliberately. The seed is in this repository, so the credential is
 * already public; refusing to name it here would protect nothing and would make
 * the demonstration harder to evaluate.
 */
const DEMO_PASSWORD = 'harbor-admin-2026';

/**
 * The seeded accounts, described by what they can do rather than by their role
 * name.
 *
 * "Admin" and "manager" mean nothing to someone who has not read the permission
 * table. "Sees the admin console" and "can change the catalogue but cannot delete
 * a product" are the same information, stated in a way that makes the role split
 * legible before signing in.
 */
const DEMO_ACCOUNTS = [
  { email: 'admin@harbor.test', label: 'Admin — full access, including deleting products' },
  { email: 'manager@harbor.test', label: 'Manager — catalogue and orders, no destructive actions' },
  { email: 'customer@harbor.test', label: 'Customer — a normal shopper, with order history' },
] as const;

export function Login() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [failure, setFailure] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const redirectTo = (location.state as { from?: string } | null)?.from ?? '/';

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setFailure(null);

    const parsed = LoginSchema.safeParse({ email, password });
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path.join('.');
        if (!next[key]) next[key] = issue.message;
      }
      setErrors(next);
      return;
    }
    setErrors({});
    setSubmitting(true);

    try {
      await signIn(parsed.data);
      navigate(redirectTo, { replace: true });
    } catch (caught) {
      setFailure(
        caught instanceof ApiError
          ? caught.problem.detail
          : 'Could not sign you in. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  /**
   * Sign in as a seeded account without typing anything.
   *
   * The seeded credentials were already printed on this page, so this does not
   * disclose anything new. What it changes is whether anyone actually sees the
   * demo: a visitor who has to copy a password off the screen and type it
   * correctly usually does not.
   */
  async function signInAsDemo(demoEmail: string) {
    setFailure(null);
    setErrors({});
    setEmail(demoEmail);
    setPassword(DEMO_PASSWORD);
    setSubmitting(true);

    try {
      await signIn({ email: demoEmail, password: DEMO_PASSWORD });
      navigate(redirectTo, { replace: true });
    } catch (caught) {
      setFailure(
        caught instanceof ApiError
          ? caught.problem.detail
          : 'The demo account could not be reached. The seed may not have been applied.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto flex max-w-md flex-col justify-center px-4 py-16 sm:px-6">
      <div className="text-center">
        <h1 className="text-2xl font-semibold tracking-tight">Welcome back</h1>
        <p className="mt-1.5 text-sm text-muted">
          Sign in to see your orders, manage sessions, and keep your basket across devices.
        </p>
      </div>

      {failure && (
        <div className="mt-6">
          <Alert tone="danger" title="Sign-in failed" onDismiss={() => setFailure(null)}>
            {failure}
          </Alert>
        </div>
      )}

      <Card className="mt-6 p-6">
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <Field label="Email" htmlFor="email" error={errors['email']}>
            <TextInput
              id="email"
              type="email"
              autoComplete="email"
              autoFocus
              required
              value={email}
              invalid={Boolean(errors['email'])}
              onChange={(event) => setEmail(event.target.value)}
            />
          </Field>

          <Field label="Password" htmlFor="password" error={errors['password']}>
            <TextInput
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              invalid={Boolean(errors['password'])}
              onChange={(event) => setPassword(event.target.value)}
            />
          </Field>

          <Button type="submit" fullWidth size="lg" loading={submitting}>
            Sign in
          </Button>
        </form>

        <p className="mt-5 text-center text-sm text-muted">
          No account?{' '}
          <Link to="/register" className="font-medium text-accent hover:underline">
            Create one
          </Link>
        </p>
      </Card>

      {/*
        Seeded credentials, shown on purpose and with a button rather than as
        text to retype.

        This is a demonstration deployment whose seed is in the repository, so
        hiding the demo logins would not make it more secure — it would only make
        the project harder to evaluate. Printing them and asking a visitor to type
        them correctly is the version of that which still loses most visitors.
      */}
      <Card className="mt-4 p-4">
        <p className="text-xs font-medium uppercase tracking-wide text-subtle">Try it without registering</p>
        <p className="mt-2 text-xs leading-relaxed text-muted">
          Three accounts at different privilege levels. The admin sees a console the
          shopper does not, which is the quickest way to see what the role split
          actually does.
        </p>

        <div className="mt-3 space-y-2">
          {DEMO_ACCOUNTS.map((account) => (
            <button
              key={account.email}
              type="button"
              disabled={submitting}
              onClick={() => void signInAsDemo(account.email)}
              className="w-full rounded-lg border border-line bg-raised px-3 py-2 text-left transition-colors hover:border-line-strong disabled:opacity-50"
            >
              <span className="block text-xs font-medium text-ink">{account.label}</span>
              <span className="tabular block text-[11px] text-subtle">{account.email}</span>
            </button>
          ))}
        </div>

        <p className="mt-3 text-[11px] text-subtle">
          Every demo account uses the password <code>harbor-admin-2026</code>. The
          accounts come from <code>seeds/seed.sql</code>, which is in the repository.
        </p>
      </Card>
    </div>
  );
}
